import type { FieldChange } from '@ben/columbia/api/benefit-configuration';
import { Code, Stack, Table, Text } from '@mantine/core';
import { Muted } from './shared';

// ─── Data types ───────────────────────────────────────────────────────────────

interface FormattedValue {
  text: string;
  /** True when the value is an object/array — renders in a monospace block. */
  isCode: boolean;
}

export interface FieldDiffRow {
  /** Original dotted path — used as the React key. */
  key: string;
  /** Human-readable field path, e.g. "sub prices · item 3 · employee contribution". */
  field: string;
  before: FormattedValue;
  after: FormattedValue;
}

// ─── Transformations ──────────────────────────────────────────────────────────

/**
 * Paths arrive as `sub_prices[2].employee_contribution`. Array indices are
 * preserved as "item N" so the reviewer can tell which record changed rather
 * than seeing stripped paths that look identical for every array item.
 *
 *   sub_prices[2].employee_contribution
 *   → sub prices · item 3 · employee contribution
 */
export const humanisePath = (path: string) =>
  path
    .replaceAll(/\[(\d+)]/g, (_, i: string) => `.item ${Number(i) + 1}`)
    .split('.')
    .filter(Boolean)
    .map((segment) => segment.replaceAll('_', ' '))
    .join(' · ');

/**
 * Scalar values as readable strings. Objects and arrays are pretty-printed so
 * the diff cell can render them in a monospace block rather than one-line JSON.
 */
export const formatValue = (value: unknown): FormattedValue => {
  if (value === null || value === undefined)
    return { text: 'Not set', isCode: false };
  if (typeof value === 'boolean')
    return { text: value ? 'Yes' : 'No', isCode: false };
  if (typeof value === 'string') return { text: value, isCode: false };
  if (typeof value === 'number') return { text: String(value), isCode: false };
  return { text: JSON.stringify(value, null, 2), isCode: true };
};

/** Map raw API field changes to display-ready rows. */
export const toFieldDiffRows = (fieldChanges: FieldChange[]): FieldDiffRow[] =>
  fieldChanges.map((fc) => ({
    key: fc.path,
    field: humanisePath(fc.path),
    before: formatValue(fc.before),
    after: formatValue(fc.after),
  }));

// ─── Component ────────────────────────────────────────────────────────────────

interface FieldDiffTableProps {
  rows: FieldDiffRow[];
  truncated: boolean;
}

/**
 * Renders a before/after diff table. Callers should transform their raw data
 * with `toFieldDiffRows` before passing it in — keeps this component focused
 * purely on rendering.
 */
export const FieldDiffTable = ({ rows, truncated }: FieldDiffTableProps) => (
  <Stack gap="xs">
    <Table withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={{ minWidth: 100 }}>Field</Table.Th>
          <Table.Th style={{ minWidth: 100 }}>Before</Table.Th>
          <Table.Th style={{ minWidth: 100 }}>After</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr key={row.key}>
            <Table.Td>
              <Text size="sm" fw={500}>
                {row.field}
              </Text>
            </Table.Td>
            <DiffCell value={row.before} />
            <DiffCell value={row.after} />
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
    {truncated ? (
      <Muted>Only the first {rows.length} differences are shown.</Muted>
    ) : null}
  </Stack>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DiffCell = ({ value }: { value: FormattedValue }) => (
  <Table.Td style={{ verticalAlign: 'top' }}>
    {value.isCode ? (
      <Code block>{value.text}</Code>
    ) : (
      <Text size="sm">{value.text}</Text>
    )}
  </Table.Td>
);
